import {useContext, useState} from 'react';

import {useFormik, FormikErrors} from 'formik';
import {useTranslation} from 'react-i18next';
import ReactPlayer from 'react-player';

import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import VideoCameraFrontIcon from '@mui/icons-material/VideoCameraFront';
import {
  TextField,
  Grid,
  InputAdornment,
  Box,
  FormHelperText,
  IconButton,
  Collapse,
  Typography,
  useTheme,
} from '@mui/material';

import {CompanyMediaService} from '../services/CompanyMediaService';
import {CompanyProfile} from '../types';

import {SnackBarContext} from '../../../../contexts/SnackBarContext/SnackBarContext';
import {getFieldStyle} from '../../../../layouts/theme/FieldStyles';

type CompanyProfileMediaVideosProps = {
  company: CompanyProfile;
};

interface FormValues {
  videoUrl?: string;
}

export const CompanyProfileMediaVideos = ({company}: CompanyProfileMediaVideosProps) => {
  const [videoUrl, setVideoUrl] = useState<any>(company?.companyMediaFiles?.videos?.at(-1)?.sourceUrl);
  const [videoResourceIds, setVideoResourceIds] = useState<any>([
    company?.companyMediaFiles?.videos?.at(-1)?.resourceId,
  ]);
  const [collapse, setCollapse] = useState<boolean>(false);
  const {dispatchMessage} = useContext(SnackBarContext);

  const mediaService = new CompanyMediaService();

  const {t} = useTranslation();
  const theme = useTheme();
  const fieldStyle = getFieldStyle(theme);

  const validate = (values: FormValues): void | FormikErrors<FormValues> => {
    const errors: FormikErrors<FormValues> = {};
    if (!values.videoUrl) {
      return;
    }
    if (!/^(ftp|http|https):\/\/[^ " ]+$/.test(values.videoUrl || '')) {
      errors.videoUrl = 'companyProfile.header.media.videos.invalidUrl';
    }
    return errors;
  };

  const createVideoMedia = async (companyId: string, mediaURL: string): Promise<any> => {
    const mediaData: any = {
      videos: [{sourceUrl: mediaURL}],
    };

    await mediaService.createCompanyMedia(companyId, mediaData).then((res) => {
      setVideoUrl(res.companyMediaFiles.videos[0].sourceUrl);
      setVideoResourceIds([res.companyMediaFiles.videos[0].resourceId]);
      dispatchMessage(t('companyProfile.header.media.videos.addedVideo'), 'success');
    });
  };

  const deleteImageMedia = async (companyId: string, mediaIds: string[]): Promise<any> => {
    await mediaService.deleteCompanyMedia(companyId, mediaIds).then(() => {
      setVideoUrl(undefined);
      setVideoResourceIds([]);
      dispatchMessage(t('companyProfile.header.media.videos.removedVideo'), 'success');
    });
  };

  const updateVideoMedia = async (companyId: string, mediaURL: string): Promise<any> => {
    const mediaData: any = {
      resourceId: videoResourceIds[0],
      sourceUrl: mediaURL,
    };

    await mediaService.updateCompanyMedia(companyId, mediaData).then((res) => {
      setVideoUrl(res.companyMediaFiles.videos[0].sourceUrl);
      setVideoResourceIds(res.companyMediaFiles.videos.map((video: any) => video.resourceId));
      dispatchMessage(t('companyProfile.header.media.videos.updatedVideo'), 'success');
    });
  };

  const formik = useFormik<FormValues>({
    initialValues: {
      videoUrl: videoUrl ?? '',
    },
    validate,

    onSubmit: async (values: FormValues) => {
      setVideoUrl(values.videoUrl);
      handleCollapse();

      if (!videoUrl && values.videoUrl) {
        await createVideoMedia(company.resourceId, values.videoUrl);
      } else if (videoUrl && values.videoUrl) {
        await updateVideoMedia(company.resourceId, values.videoUrl);
      }
    },
  });

  const handleCollapse = () => {
    setCollapse(!collapse);
  };

  return (
    <>
      <form onSubmit={formik.handleSubmit}>
        <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
          {videoUrl ? (
            <>
              <Grid item container xs={12} display='flex' justifyContent={'center'}>
                <ReactPlayer
                  url={videoUrl}
                  controls={true}
                  light={true}
                  style={{
                    borderRadius: '12px',
                    boxShadow: '0 1px 4px rgba(9, 44, 76, 0.06), 0 2px 12px rgba(9, 44, 76, 0.04)',
                  }}
                  onError={() => {
                    setVideoUrl(undefined);
                    const message = t('companyProfile.header.media.videos.error');
                    const severity = 'error';
                    dispatchMessage(message, severity);
                  }}
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid container direction='column' alignItems='center' mb={2} sx={{opacity: '0.3'}}>
                <Typography>{t('companyProfile.header.media.videos.emptyState')}</Typography>
              </Grid>
            </>
          )}
        </Box>
        <Grid item display={'flex'} justifyContent={'flex-end'}>
          <IconButton aria-label='video-camera' onClick={handleCollapse}>
            <VideoCameraFrontIcon />
          </IconButton>
        </Grid>
        <Grid item sx={{justifyContent: 'center'}}>
          <Collapse in={collapse} orientation='vertical'>
            {/* TODO: Should be changed to EmpionTextField, when the InputAdornment issue is resolved */}
            <TextField
              label='Video URL'
              name='videoUrl'
              id='videoUrl'
              value={formik.values.videoUrl}
              onChange={formik.handleChange}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <IconButton type='submit'>
                      <PublishIcon />
                    </IconButton>
                    {videoUrl && (
                      <IconButton
                        onClick={async () => {
                          await deleteImageMedia(company.resourceId, videoResourceIds);
                          formik.setFieldValue('videoUrl', '');
                          handleCollapse();
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{marginTop: 2, ...fieldStyle}}
            />
            {formik.errors.videoUrl && <FormHelperText error>{t(formik.errors.videoUrl)}</FormHelperText>}
          </Collapse>
        </Grid>
      </form>
    </>
  );
};
